import { DefaultAdFormatValues } from './adFormat.shared'
import { DeviceType } from './device-type'

export const AD_UNIT_MEDIATYPES = ['Banner', 'Outstream', 'Native', 'Instream'] as const
export type Mediatype = (typeof AD_UNIT_MEDIATYPES)[number]

export type SizesByDevice = {
  size: string
  devices: DeviceType[]
}
export type AdFormat = {
  id: string
  name: string
  sizes: SizesByDevice[]
  mediatypes: Mediatype[]
  devices: DeviceType[]
  isDynamicToggleEnabled?: boolean
  isParallaxToggleEnabled?: boolean
  isAnchorToggleEnabled?: boolean
  defaultValues?: DefaultAdFormatValues
}
export type AdFormatForApps = Pick<AdFormat, 'id' | 'name' | 'mediatypes'>

export const SKIN_SIZE = '1800x1000'

// STANDARD
export const LEADERBOARD: AdFormat = {
  id: 'LEADERBOARD',
  name: 'Leaderboard',
  sizes: [
    { size: '728x90', devices: ['desktop'] },
    { size: '1000x90', devices: ['desktop'] },
    { size: '468x60', devices: ['desktop'] },
    { size: '970x250', devices: ['desktop'] },
    { size: '970x90', devices: ['desktop'] },
    { size: '1000x300', devices: ['desktop'] },
  ],
  mediatypes: ['Banner', 'Native'],
  devices: ['desktop'],
  isDynamicToggleEnabled: false,
  defaultValues: {
    dynamicEnabled: false,
    parallaxEnabled: false,
    anchor: undefined,
  },
}
export const SIDEBAR: AdFormat = {
  id: 'SIDEBAR',
  name: 'Sidebar',
  sizes: [
    { size: '300x600', devices: ['desktop'] },
    { size: '300x250', devices: ['desktop'] },
    { size: '160x600', devices: ['desktop'] },
    { size: '120x600', devices: ['desktop'] },
  ],
  mediatypes: ['Banner', 'Native'],
  devices: ['desktop'],
  isDynamicToggleEnabled: true,
  defaultValues: {
    parallaxEnabled: false,
    anchor: undefined,
  },
}
export const RECTANGLE_MEDIUM: AdFormat = {
  id: 'RECTANGLE_MEDIUM',
  name: 'Rectangle medium',
  sizes: [
    { size: '300x250', devices: ['desktop', 'mobile'] },
    { size: '250x250', devices: ['desktop', 'mobile'] },
  ],
  mediatypes: ['Banner', 'Outstream', 'Native'],
  devices: ['desktop', 'mobile'],
  isDynamicToggleEnabled: true,
  defaultValues: {
    parallaxEnabled: false,
    anchor: undefined,
  },
}
export const RECTANGLE_LARGE: AdFormat = {
  id: 'RECTANGLE_LARGE',
  name: 'Rectangle large',
  sizes: [
    { size: '300x600', devices: ['desktop'] },
    { size: '300x250', devices: ['desktop'] },
    { size: '250x250', devices: ['desktop'] },
    { size: '120x600', devices: ['desktop'] },
    { size: '160x600', devices: ['desktop'] },
  ],
  mediatypes: ['Banner', 'Outstream', 'Native'],
  devices: ['desktop'],
  isDynamicToggleEnabled: true,
  defaultValues: {
    parallaxEnabled: false,
    anchor: undefined,
  },
}
export const OUTSTREAM_SINGLE_AD: AdFormat = {
  id: 'OUTSTREAM_SINGLE_AD',
  name: 'Outstream',
  mediatypes: ['Outstream'],
  devices: ['desktop', 'mobile'],
  sizes: [],
  isDynamicToggleEnabled: false,
  defaultValues: {
    dynamicEnabled: false,
    parallaxEnabled: false,
    anchor: undefined,
  },
}
export const MOBILE_BANNER: AdFormat = {
  id: 'MOBILE_BANNER',
  name: 'Mobile banner',
  sizes: [
    { size: '320x100', devices: ['mobile'] },
    { size: '320x50', devices: ['mobile'] },
    { size: '300x100', devices: ['mobile'] },
    { size: '300x50', devices: ['mobile'] },
  ],
  mediatypes: ['Banner', 'Native'],
  devices: ['mobile'],
  isDynamicToggleEnabled: true,
  defaultValues: {
    parallaxEnabled: false,
    anchor: undefined,
  },
}
export const IN_CONTENT: AdFormat = {
  id: 'IN_CONTENT',
  name: 'In content',
  sizes: [
    { size: '300x600', devices: ['desktop'] },
    { size: '300x250', devices: ['desktop', 'mobile'] },
    { size: '320x100', devices: ['mobile'] },
    { size: '320x50', devices: ['mobile'] },
    { size: '320x480', devices: ['mobile'] },
    { size: '336x280', devices: ['desktop'] },
  ],
  mediatypes: ['Banner', 'Outstream', 'Native'],
  devices: ['desktop', 'mobile'],
  isDynamicToggleEnabled: true,
  defaultValues: {
    parallaxEnabled: false,
    anchor: undefined,
  },
}
export const NATIVE_ONLY: AdFormat = {
  id: 'NATIVE_ONLY',
  name: 'Native only',
  sizes: [],
  mediatypes: ['Native'],
  devices: ['desktop', 'mobile'],
  isDynamicToggleEnabled: true,
  defaultValues: {
    parallaxEnabled: false,
    anchor: undefined,
    nativeTemplate: '528d1de0-027a-45f3-b079-a216dc5450d7', // 300x250 ID
  },
}

// HIGH IMPACT
export const INTERSTITIAL: AdFormat = {
  id: 'INTERSTITIAL',
  name: 'Interstitial',
  mediatypes: ['Banner', 'Outstream'],
  devices: ['desktop', 'mobile'],
  sizes: [],
  isDynamicToggleEnabled: true,
  defaultValues: {
    parallaxEnabled: false,
    anchor: undefined,
  },
}
export const MOBILE_INTERSTITIAL: AdFormat = {
  id: 'MOBILE_INTERSTITIAL',
  name: 'Mobile interstitial',
  mediatypes: ['Banner', 'Outstream'],
  devices: ['mobile'],
  sizes: [],
  isDynamicToggleEnabled: true,
  defaultValues: {
    parallaxEnabled: false,
    anchor: undefined,
  },
}
export const OUT_OF_PAGE: AdFormat = {
  id: 'OUT_OF_PAGE',
  name: 'Out-of-page',
  mediatypes: ['Banner', 'Outstream'],
  devices: ['desktop', 'mobile'],
  sizes: [],
  isDynamicToggleEnabled: true,
  defaultValues: {
    parallaxEnabled: false,
    anchor: undefined,
  },
}
export const SKIN_SINGLE_AD: AdFormat = {
  id: 'SKIN_SINGLE_AD',
  name: 'Skin single ad ',
  sizes: [{ size: SKIN_SIZE, devices: ['desktop'] }],
  mediatypes: ['Banner'],
  devices: ['desktop'],
  isDynamicToggleEnabled: false,
  // specifying the max size filter to undefined allows Skin to remove the existing max size filter
  defaultValues: { parallaxEnabled: false, anchor: undefined, dynamicEnabled: false, containerMaxSize: { width: undefined, height: undefined }, skinEnabled: true },
}
export const CUSTOM_AD_FORMAT: AdFormat = {
  id: 'CUSTOM',
  name: 'Custom',
  mediatypes: ['Banner', 'Outstream', 'Native'],
  devices: ['desktop', 'mobile'],
  sizes: [],
  isDynamicToggleEnabled: true,
  isParallaxToggleEnabled: true,
  isAnchorToggleEnabled: true,
}

export const IN_CONTENT_PARALLAX: AdFormat = {
  id: 'IN_CONTENT_PARALLAX',
  name: 'In content parallax',
  sizes: [
    { size: '300x600', devices: ['mobile', 'desktop'] },
    { size: '300x250', devices: ['mobile', 'desktop'] },
    { size: '320x480', devices: ['mobile', 'desktop'] },
    { size: '336x280', devices: ['mobile', 'desktop'] },
    { size: '320x50', devices: ['mobile'] },
    { size: '320x100', devices: ['mobile'] },
  ],
  mediatypes: ['Banner', 'Outstream', 'Native'],
  devices: ['mobile', 'desktop'],
  defaultValues: {
    parallaxEnabled: true,
    anchor: undefined,
    fluid: true,
    containerMaxSize: {
      height: undefined,
    },
  },
  isDynamicToggleEnabled: true,
}

export const ANCHOR: AdFormat = {
  id: 'ANCHOR',
  name: 'Anchor',
  sizes: [
    { size: '320x100', devices: ['mobile'] },
    { size: '320x50', devices: ['mobile'] },
    { size: '300x100', devices: ['mobile'] },
    { size: '300x50', devices: ['mobile'] },
    { size: '728x90', devices: ['desktop'] },
    { size: '1000x90', devices: ['desktop'] },
    { size: '468x60', devices: ['desktop'] },
    { size: '970x90', devices: ['desktop'] },
  ],
  mediatypes: ['Banner', 'Native'],
  devices: ['mobile', 'desktop'],
  defaultValues: {
    dynamicEnabled: false,
    parallaxEnabled: false,
    anchor: { mode: 'useExistingDiv' },
  },
  isDynamicToggleEnabled: true,
}
export const INSTREAM: AdFormat = {
  id: 'INSTREAM',
  name: 'Instream',
  mediatypes: ['Instream'],
  devices: ['desktop', 'mobile'],
  sizes: [{ size: '640x480', devices: ['desktop', 'mobile'] }],
  isDynamicToggleEnabled: false,
  defaultValues: {
    dynamicEnabled: false,
    parallaxEnabled: false,
    anchor: undefined,
  },
}

export const APP_INTERSTITIAL: AdFormat = {
  id: 'APP_INTERSTITIAL',
  name: 'App Interstitial',
  mediatypes: ['Banner', 'Outstream'],
  devices: ['mobile'],
  sizes: [],
}

export const APP_BANNER: AdFormat = {
  id: 'APP_BANNER',
  name: 'Banner',
  mediatypes: ['Banner', 'Native'],
  devices: ['mobile'],
  sizes: [],
}

export const APP_INSTREAM: AdFormat = {
  id: 'APP_INSTREAM',
  name: 'Instream',
  mediatypes: ['Instream'],
  devices: ['mobile'],
  sizes: [],
}

export const APP_NATIVE: AdFormat = {
  id: 'APP_NATIVE',
  name: 'Native',
  mediatypes: ['Native'],
  devices: ['mobile'],
  sizes: [],
}

export const AD_FORMATS: AdFormat[] = [
  INTERSTITIAL,
  LEADERBOARD,
  SIDEBAR,
  RECTANGLE_MEDIUM,
  RECTANGLE_LARGE,
  OUTSTREAM_SINGLE_AD,
  MOBILE_BANNER,
  IN_CONTENT,
  MOBILE_INTERSTITIAL,
  OUT_OF_PAGE,
  INSTREAM,
  SKIN_SINGLE_AD,
  CUSTOM_AD_FORMAT,
  IN_CONTENT_PARALLAX,
  ANCHOR,
  NATIVE_ONLY,
  APP_INTERSTITIAL,
  APP_BANNER,
  APP_NATIVE,
]
